<template>
	<div>
		<label
			v-if="label"
			class="text-sm text-gray-dark mb-1"
			:class="{
				'text-red-700': props.errors?.length
			}"
			:for="name"
		>
			{{ label }}
			<span v-if="labelRequired">*</span>
		</label>
		<Listbox
			:id="name"
			v-model="value"
			:class="[
				'flex relative',
				!props.compact && 'border px-3 py-2',
				props.errors?.length
					? 'text-red-700 border-red-700 ring-red-500'
					: 'border-gray-300 ring-gray-dark',
				props.btnWrapStyles
			]"
			as="div"
		>
			<ListboxButton
				:id="'button-list-box'"
				class="relative w-full cursor-default pr-8 bg-transparent cursor-pointer text-sm"
				:class="props.btnStyles"
			>
				<div class="flex justify-center gap-1">
					<p
						:class="{
							'block text-left': true,
							'opacity-50': !getValueOption?.label
						}"
					>
						<span class="flex items-center">
							<img
								v-if="flags"
								:src="`/flags/${getValueOption?.value}.svg`"
								alt="Flag"
								class="w-[30px] h-[20px] mr-2"
							/>
							{{
								getValueOption?.iso ||
								getValueOption?.label ||
								props.placeholder
							}}
						</span>
					</p>
					<span class="pointer-events-none inset-y-0 flex items-center">
						<ChevronDownIcon class="h-5 w-5 text-current" />
					</span>
				</div>
			</ListboxButton>

			<transition
				appear
				enter-active-class="transition duration-200"
				enter-from-class="opacity-0"
				enter-to-class="opacity-100"
				leave-active-class="transition ease-in duration-100"
				leave-from-class="opacity-100"
				leave-to-class="opacity-0"
			>
				<ListboxOptions
					class="absolute min-w-64 w-full trans z-30 mt-1 max-h-72 overflow-hidden bg-white rounded-lg py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 outline-none focus:outline-none sm:text-sm"
					:class="{
						'-bottom-2 translate translate-y-full right-0':
							position === 'bottom-right',
						'-top-2 translate -translate-y-full right-0':
							position === 'top-right',
						'-bottom-2 translate translate-y-full left-0':
							position === 'bottom-left',
						'-top-2 translate -translate-y-full left-0': position === 'top-left'
					}"
				>
					<ListboxOption
						v-for="item in options"
						:key="item.value"
						v-slot="{ active, selected }"
						as="template"
						:value="item.value"
					>
						<li
							:class="[
								active ? 'bg-secondary text-white' : 'text-gray-900',
								'relative cursor-pointer select-none py-4 px-3'
							]"
						>
							<span
								:class="[
									selected ? 'font-semibold mr-3' : 'font-normal',
									'block'
								]"
							>
								<img
									v-if="flags"
									:src="`/flags/${item?.value}.svg`"
									alt="Flag"
									class="w-[20px] h-[10px] mr-1 mt-[6px] float-left"
								/>
								{{ item.label }}
							</span>
							<span
								v-if="selected"
								:class="[
									active ? 'text-white' : 'text-gray-dark',
									'absolute inset-y-0 right-0 flex items-center pr-4'
								]"
							>
								<CheckIcon class="h-5 w-5" aria-hidden="true" />
							</span>
						</li>
					</ListboxOption>
				</ListboxOptions>
			</transition>
		</Listbox>
	</div>
</template>

<script setup lang="ts">
import {
	Listbox,
	ListboxButton,
	ListboxOption,
	ListboxOptions
} from "@headlessui/vue";
import { CheckIcon, ChevronDownIcon } from "@heroicons/vue/20/solid";

const props = withDefaults(
	defineProps<{
		name?: string;
		label?: string;
		labelRequired?: boolean;
		compact?: boolean;
		modelValue?: string;
		flags?: boolean;
		options: {
			label: string;
			value: string;
			iso?: string;
		}[];
		btnStyles?: string;
		btnWrapStyles?: string;
		placeholder?: string;
		errors?: string[];
		position?: "top-left" | "top-right" | "bottom-right" | "bottom-left";
	}>(),
	{
		compact: true,
		position: "bottom-right"
	}
);
const value = ref("");
const emit = defineEmits(["update:modelValue"]);

const getValueOption = computed(() => {
	return props.options?.find((x) => x.value === value?.value);
});

watch(value, (v) => {
	if (props.modelValue !== v) {
		emit("update:modelValue", v);
	}
});

watch(
	() => props.modelValue,
	(v) => {
		if (v && value.value !== v) {
			value.value = v;
		}
	},
	{
		immediate: true
	}
);
</script>
